import * as THREE from 'three';
import { GLTFLoader, type GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import type { GlbSceneConfig, GlbModelEntry, GlbPreviewConfig } from './glbSceneConfig';
import { GLB_BG_THEME_HEX } from './glbSceneConfig';
import { MeshBVHCollisionSystem, buildCollisionBVH } from '../collision/MeshBVHCollisionSystem';
import { PreviewModel } from '../appearance/PreviewModel';
import { PrincipalPointController } from '../camera/PrincipalPointController';
import { PoiMarkerManager } from '../poi/PoiMarkerManager';
import { defaultPoiConfig } from '../../poiConfig';
import type { CameraConfig } from '../../cameraConfig';
import type { Sight, SightId } from '../../types';
import { userSelectedSight } from '../../state/appState';
import { assistantPoiClickRequested } from '../../state/assistantHud';
import { $appConfig } from '../../state/appConfig';
import { trackWebGlContextLost } from '../../analytics/performanceTracker';
import {
  sceneLoadingStarted,
  sceneLoadingProgressed,
  scenePreviewLoadingFinished,
  sceneLoadingFinished,
} from '../../state/sceneLoading';
import {
  incrementWebGLContext,
  decrementWebGLContext,
  incrementScene,
  decrementScene,
  registerMixer,
  unregisterMixer,
  registerPoiManager,
  unregisterPoiManager,
  registerPreviewModel,
  unregisterPreviewModel,
  logEvent,
} from '../../debug/memoryTracker';
import { acquireMainRenderer, releaseMainRenderer } from '../mainRenderer';
import { disposeMaterialTextures } from '../utils/disposeSceneResources';
import { loadWithCdnFallback } from '../../utils/loadWithCdnFallback';
import { fetchWithTtfbTimeout } from '../../utils/fetchWithTtfbTimeout';
import {
  installDumpHook,
  logArtifactPhase,
  scheduleFinalizationCheck,
  trackTreeForFinalization,
  dumpFinalizationStats,
} from '../../debug/artifactSwapTracker';

// Shared loaders — avoid creating new Web Workers on every scene instantiation
let sharedDracoLoader: DRACOLoader | null = null;
let sharedGltfLoader: GLTFLoader | null = null;

export const getSharedLoaders = () => {
  if (!sharedDracoLoader) {
    sharedDracoLoader = new DRACOLoader();
    sharedDracoLoader.setDecoderPath('/draco/');
  }
  if (!sharedGltfLoader) {
    sharedGltfLoader = new GLTFLoader();
    sharedGltfLoader.setDRACOLoader(sharedDracoLoader);
  }
  return { loader: sharedGltfLoader, dracoLoader: sharedDracoLoader };
};

export interface GlbSceneOptions {
  container: HTMLElement;
  config: GlbSceneConfig;
  sights?: Sight[];
  onProgress?: (loaded: number, total: number | null) => void;
  onPreviewReady?: () => void;
  onReady?: () => void;
  onError?: (error: unknown) => void;
  /** When true, does not fire global sceneLoading* events (used for PoiArtifact overlay). */
  suppressLoadingEvents?: boolean;
  /** When true, skip $appConfig camera viewOffset subscription (fullscreen scenes without chat panel). */
  ignoreViewOffset?: boolean;
  /**
   * When true, renderer uses `container.clientWidth/Height` instead of window size.
   * Used for artifact claim'ов через `poiArtifactHost` (canvas переезжает между
   * card-container и fullscreen-overlay; размер определяется текущим container'ом).
   */
  useContainerSize?: boolean;
}

export interface GlbSceneApi {
  dispose: () => void;
  /** Pause render loop (keep renderer/context alive). Used by poiArtifactHost cache. */
  pause: () => void;
  /** Resume render loop. Optional new container to re-attach canvas + re-observe resize. */
  resume: (newContainer?: HTMLElement) => void;
  /**
   * Swap scene content (models + collision + camera position + controls limits)
   * without tearing down WebGLRenderer. Used by poiArtifactHost to switch between
   * artifact scenes (e.g. black-stone → golden-spout) without creating a new
   * WebGL context — avoids iOS OOM from async `forceContextLoss()` overlap.
   *
   * Requirements for `newConfig`:
   *   - `preview` must be null/absent (swap path does not support reveal animation).
   *   - `fov/near/far/clearColor/lighting` assumed shell-compatible (shared across
   *     artifacts). Only camera position, controls limits, models, collision are
   *     replaced.
   *
   * Race safety: calling swapContent again before previous load completes cancels
   * the previous load (generation counter). onReady only fires for the latest call.
   */
  swapContent: (newConfig: GlbSceneConfig, onReady?: () => void) => void;
  /**
   * Выгрузить весь content (модели, mixers, collision, poiManager, previewModel,
   * groundPlane) и приостановить render loop. Shell (renderer, scene корневая,
   * camera, controls, lighting, envMap) остаётся живым — никакого
   * `forceContextLoss`, никакой потери контекста.
   *
   * Используется `poiArtifactHost` при `releasePoiArtifact('fullscreen')`, чтобы
   * освободить GPU-память содержимого (~50-100MB текстур модели), пока артефакт
   * не нужен. При следующем open — `swapContent(newConfig)` загружает контент
   * на освобождённой GPU, без overlap'а старого и нового.
   */
  standby: () => void;
  scene: THREE.Scene;
  renderer: THREE.WebGLRenderer;
  camera: THREE.PerspectiveCamera;
  controls: OrbitControls;
  hemisphereLight: THREE.HemisphereLight;
  directionalLights: THREE.DirectionalLight[];
  modelRoots: Map<string, THREE.Object3D>;
  groundPlane: THREE.Mesh | null;
  collisionSystem: MeshBVHCollisionSystem | null;
  collisionMesh: THREE.Mesh | null;
}

const DEFAULT_REVEAL_DURATION = 2.0;
const PREVIEW_AUTO_ROTATE_SPEED = 2.0;
const MIN_PREVIEW_DISPLAY_MS = 4000;

function createPreviewModel(
  threeScene: THREE.Scene,
  previewCfg: GlbPreviewConfig,
  reportLoadingEvents: boolean,
): PreviewModel {
  const transform: Partial<{ position: THREE.Vector3; rotation: THREE.Euler; scale: THREE.Vector3 }> = {};
  if (previewCfg.transform?.position) {
    const p = previewCfg.transform.position;
    transform.position = new THREE.Vector3(p.x, p.y, p.z);
  }
  if (previewCfg.transform?.rotation) {
    const r = previewCfg.transform.rotation;
    transform.rotation = new THREE.Euler(r.x, r.y, r.z);
  }
  if (previewCfg.transform?.scale) {
    const s = previewCfg.transform.scale;
    transform.scale = new THREE.Vector3(s.x, s.y, s.z);
  }

  return new PreviewModel({
    scene: threeScene,
    options: {
      url: previewCfg.url,
      transform,
      onLoadProgress: reportLoadingEvents
        ? (event) => {
            sceneLoadingProgressed({
              loadedBytes: event.loadedBytes,
              totalBytes: event.totalBytes,
            });
          }
        : undefined,
      style: { ...previewCfg.style },
    },
  });
}

export function createGlbScene({
  container: initialContainer,
  config,
  sights = [],
  onProgress,
  onPreviewReady,
  onReady,
  onError,
  suppressLoadingEvents = false,
  ignoreViewOffset = false,
  useContainerSize = false,
}: GlbSceneOptions): GlbSceneApi {
  let disposed = false;
  let paused = false;
  let frameId = 0;
  let container = initialContainer;
  const hasPreview = config.preview != null;

  const poiArtifactOverlay = Boolean(suppressLoadingEvents && ignoreViewOffset);
  logEvent('createGlbScene start', {
    sceneId: config.id,
    poiArtifact: poiArtifactOverlay,
    hasPreview,
    modelCount: config.models.length,
  });
  incrementScene('glb');

  if (hasPreview && !suppressLoadingEvents) {
    sceneLoadingStarted();
  }

  // --- Viewport helpers (match splat: renderer sized to window, container clips) ---
  const urlParams = new URL(window.location.href).searchParams;
  const isMobilePreview = urlParams.get('viewMode') === 'mobile';
  const mobilePreviewScreen = isMobilePreview
    ? (() => {
        let el = container.parentElement;
        while (el) {
          if (getComputedStyle(el).transform !== 'none') return el;
          el = el.parentElement;
        }
        return null;
      })()
    : null;
  const getViewportWidth = () =>
    useContainerSize
      ? Math.max(1, container.clientWidth)
      : mobilePreviewScreen
      ? mobilePreviewScreen.clientWidth
      : window.innerWidth;
  const getViewportHeight = () =>
    useContainerSize
      ? Math.max(1, container.clientHeight)
      : mobilePreviewScreen
      ? mobilePreviewScreen.clientHeight
      : window.innerHeight;

  // --- Renderer ---
  // PoiArtifact overlay keeps its OWN dedicated renderer (antialias:true,
  // crisp model rendering for detail view). Top-level GLB scenes (e.g. mina)
  // share the app-wide main renderer to avoid iOS WebGL context accumulation.
  const mainRendererClaimantId = `glb-${config.id}`;
  const renderer = poiArtifactOverlay
    ? new THREE.WebGLRenderer({ antialias: true })
    : acquireMainRenderer(mainRendererClaimantId, container);
  const maxPr = config.renderer.maxPixelRatio ?? 2;
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, maxPr));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = (config.renderer.toneMapping ?? THREE.NoToneMapping) as THREE.ToneMapping;
  renderer.toneMappingExposure = config.renderer.toneMappingExposure ?? 1.0;
  // Transmission render target is allocated at viewport × DPR × this scale.
  // 0.5 = ×4 less VRAM for glass/transmission materials (maqam). Visually imperceptible
  // because transmission is inherently blurred.
  renderer.transmissionResolutionScale = 0.5;
  const themeHex = config.bgTheme != null ? GLB_BG_THEME_HEX[config.bgTheme] : null;
  const resolvedClearColor = themeHex ?? config.renderer.clearColor ?? 0x000000;
  const resolvedBgColor = themeHex ?? config.environment.backgroundColor ?? resolvedClearColor;
  renderer.setClearColor(hasPreview ? 0x000000 : resolvedClearColor);
  // Dropped before the teardown's own `forceContextLoss`, which is no crash.
  let stopContextLossTracking: (() => void) | null = null;
  if (poiArtifactOverlay) {
    // Dedicated renderer path: register with context tracker, attach canvas,
    // install webglcontextlost listener. Shared-renderer path is handled by
    // acquireMainRenderer above.
    incrementWebGLContext('glb');
    stopContextLossTracking = trackWebGlContextLost(renderer.domElement);
    container.appendChild(renderer.domElement);
    renderer.domElement.style.position = 'absolute';
    renderer.domElement.style.inset = '0';
  }

  // --- Scene ---
  const scene = new THREE.Scene();
  const mainBgColor = new THREE.Color(resolvedBgColor);
  scene.background = hasPreview ? new THREE.Color(0x000000) : mainBgColor;

  // --- Camera ---
  const { camera: camCfg } = config;
  const previewCam = config.preview;
  let canvasWidth = getViewportWidth();
  let canvasHeight = getViewportHeight();
  const camera = new THREE.PerspectiveCamera(
    camCfg.fov,
    canvasWidth / Math.max(canvasHeight, 1),
    camCfg.near,
    camCfg.far,
  );
  const startPos = previewCam?.cameraPosition ?? camCfg.initialPosition;
  const startTarget = previewCam?.cameraTarget ?? camCfg.initialTarget ?? { x: 0, y: 0, z: 0 };
  camera.position.set(startPos.x, startPos.y, startPos.z);

  // --- Controls ---
  const { controls: ctrlCfg } = config;
  const controls = new OrbitControls(camera, renderer.domElement);
  controls.target.set(startTarget.x, startTarget.y, startTarget.z);
  controls.minDistance = ctrlCfg.minDistance;
  controls.maxDistance = ctrlCfg.maxDistance;
  if (ctrlCfg.minPolarAngle != null) controls.minPolarAngle = ctrlCfg.minPolarAngle;
  if (ctrlCfg.maxPolarAngle != null) controls.maxPolarAngle = ctrlCfg.maxPolarAngle;
  // Azimuth limits are relative to the initial camera direction
  if (ctrlCfg.minAzimuthAngle != null || ctrlCfg.maxAzimuthAngle != null) {
    const initialAzimuth = Math.atan2(
      startPos.x - startTarget.x,
      startPos.z - startTarget.z,
    );
    if (ctrlCfg.minAzimuthAngle != null) controls.minAzimuthAngle = ctrlCfg.minAzimuthAngle + initialAzimuth;
    if (ctrlCfg.maxAzimuthAngle != null) controls.maxAzimuthAngle = ctrlCfg.maxAzimuthAngle + initialAzimuth;
  }
  if (ctrlCfg.enablePan != null) controls.enablePan = ctrlCfg.enablePan;
  if (ctrlCfg.enableZoom != null) controls.enableZoom = ctrlCfg.enableZoom;
  controls.enableDamping = ctrlCfg.enableDamping ?? true;
  controls.dampingFactor = ctrlCfg.dampingFactor ?? 0.05;
  if (ctrlCfg.autoRotate != null) controls.autoRotate = ctrlCfg.autoRotate;
  if (ctrlCfg.autoRotateSpeed != null) controls.autoRotateSpeed = ctrlCfg.autoRotateSpeed;
  controls.update();

  // --- Lighting ---
  const { environment: envCfg } = config;
  const hemiLight = new THREE.HemisphereLight(
    envCfg.hemisphereLight.skyColor,
    envCfg.hemisphereLight.groundColor,
    envCfg.hemisphereLight.intensity,
  );
  scene.add(hemiLight);

  const directionalLights: THREE.DirectionalLight[] = [];
  for (const dlCfg of envCfg.directionalLights) {
    const light = new THREE.DirectionalLight(dlCfg.color, dlCfg.intensity);
    light.position.set(dlCfg.position.x, dlCfg.position.y, dlCfg.position.z);
    scene.add(light);
    directionalLights.push(light);
  }

  // --- Environment map for PBR materials (neutral studio lighting) ---
  const pmremGenerator = new THREE.PMREMGenerator(renderer);
  const roomEnv = new RoomEnvironment();
  const envRenderTarget = pmremGenerator.fromScene(roomEnv);
  scene.environment = envRenderTarget.texture;
  pmremGenerator.dispose();
  roomEnv.traverse((obj) => {
    if (obj instanceof THREE.Mesh) {
      obj.geometry?.dispose();
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
      for (const m of mats) m?.dispose?.();
    }
  });
  if (config.environment.envMapIntensity != null) {
    scene.environmentIntensity = config.environment.envMapIntensity;
  }

  // --- Ground Plane ---
  let groundPlane: THREE.Mesh | null = null;
  if (config.groundPlane) {
    const gpCfg = config.groundPlane;
    const gpGeometry = new THREE.PlaneGeometry(gpCfg.size, gpCfg.size);
    const gpMaterial = new THREE.MeshStandardMaterial({
      color: gpCfg.color,
      transparent: (gpCfg.opacity ?? 1) < 1,
      opacity: gpCfg.opacity ?? 1,
    });

    if (gpCfg.textureUrl) {
      const textureLoader = new THREE.TextureLoader();
      textureLoader.load(gpCfg.textureUrl, (texture) => {
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.RepeatWrapping;
        const repeat = gpCfg.textureRepeat ?? 1;
        texture.repeat.set(repeat, repeat);
        gpMaterial.map = texture;
        gpMaterial.needsUpdate = true;
      });
    }

    groundPlane = new THREE.Mesh(gpGeometry, gpMaterial);
    groundPlane.rotation.x = -Math.PI / 2;
    groundPlane.position.y = gpCfg.positionY ?? 0;
    scene.add(groundPlane);
  }

  // --- Clock (needed by PrincipalPointController and animation mixers) ---
  const clock = new THREE.Clock();

  // --- Principal point controller (camera offset for chat panel) ---
  const principalPointController = new PrincipalPointController(camera, clock);

  // --- $appConfig subscription for camera view offset ---
  // Mirrors splat scene pattern: renderer is sized to window, container clips.
  // viewOffset from $appConfig is applied directly — no adjustment needed.
  // When ignoreViewOffset is true (fullscreen overlay), skip the subscription entirely.
  let hasAppliedInitialCameraViewOffset = false;
  let latestViewOffset: CameraConfig['viewOffset'] = $appConfig.getState().camera.viewOffset;

  const stopConfigWatch = ignoreViewOffset
    ? undefined
    : $appConfig.watch((nextConfig) => {
        const { animate: animateViewOffset, animationDurationMs, ...viewOffset } = nextConfig.camera.viewOffset;
        latestViewOffset = nextConfig.camera.viewOffset;

        const willAnimate = animateViewOffset ?? hasAppliedInitialCameraViewOffset;
        const duration = typeof animationDurationMs === 'number'
          ? Math.max(0, animationDurationMs) / 1000
          : undefined;

        principalPointController.applyViewOffset(viewOffset, {
          animate: willAnimate,
          duration,
        });

        if (!hasAppliedInitialCameraViewOffset) {
          hasAppliedInitialCameraViewOffset = true;
        }
      });

  // --- Resize (renderer sized to window, like splat) ---
  const setRendererSize = () => {
    const vw = getViewportWidth();
    const vh = getViewportHeight();
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, maxPr));
    renderer.setSize(vw, vh);
    const canvas = renderer.domElement;
    canvasWidth = canvas.clientWidth || vw;
    canvasHeight = canvas.clientHeight || vh;
    camera.aspect = canvasWidth / Math.max(1, canvasHeight);
    camera.updateProjectionMatrix();
    principalPointController.setCanvasSize(canvasWidth, canvasHeight);
  };

  const onResize = () => {
    setRendererSize();
    if (!ignoreViewOffset) {
      // Skip instant re-apply while camera offset is animating — the animation
      // target is already correct. ResizeObserver fires on every CSS transition
      // frame of container bottom, and an instant applyViewOffset would kill the
      // ongoing smooth animation causing a visible camera jump.
      if (!principalPointController.isAnimating()) {
        const { animate: _, animationDurationMs: __, ...vo } = latestViewOffset;
        principalPointController.applyViewOffset(vo, { animate: false });
      }
      principalPointController.forceSync();
    }
  };

  setRendererSize();
  const resizeObserver = new ResizeObserver(onResize);
  resizeObserver.observe(container);

  // --- Animation mixer ---
  const mixers: { mixer: THREE.AnimationMixer; root: THREE.Object3D; clips: THREE.AnimationClip[] }[] = [];

  // --- Load models ---
  const { loader } = getSharedLoaders();

  const applyTransform = (obj: THREE.Object3D, entry: GlbModelEntry) => {
    const t = entry.transform;
    if (!t) return;
    if (t.position) obj.position.set(t.position.x, t.position.y, t.position.z);
    if (t.rotation) obj.rotation.set(t.rotation.x, t.rotation.y, t.rotation.z);
    if (t.scale) obj.scale.set(t.scale.x, t.scale.y, t.scale.z);
  };

  let loadedCount = 0;
  const totalCount = config.models.length;
  const modelRoots = new Map<string, THREE.Object3D>();

  const objLoader = new OBJLoader();

  const loadModel = async (entry: GlbModelEntry): Promise<void> => {
    const isObj = entry.url.toLowerCase().endsWith('.obj');

    const buffer = await loadWithCdnFallback(
      entry.url,
      async (url) => {
        const response = await fetchWithTtfbTimeout(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch ${url}: ${response.status} ${response.statusText}`);
        }
        return response.arrayBuffer();
      },
      { assetType: 'glb', sceneId: config.id },
    );
    if (disposed) return;

    if (isObj) {
      const text = new TextDecoder().decode(buffer);
      const group = objLoader.parse(text);
      if (disposed) return;
      const material = new THREE.MeshStandardMaterial({ color: 0x888888 });
      group.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.material = material;
        }
      });
      applyTransform(group, entry);
      if (hasPreview) group.visible = false;
      scene.add(group);
      modelRoots.set(entry.key, group);

      loadedCount++;
      onProgress?.(loadedCount, totalCount);
      return;
    }

    const gltf = await new Promise<GLTF>((resolve, reject) => {
      loader.parse(buffer, '', resolve, reject);
    });
    if (disposed) return;
    applyTransform(gltf.scene, entry);
    if (hasPreview) gltf.scene.visible = false;
    scene.add(gltf.scene);
    modelRoots.set(entry.key, gltf.scene);

    if (entry.playAnimations && gltf.animations.length > 0) {
      const mixer = new THREE.AnimationMixer(gltf.scene);
      for (const clip of gltf.animations) {
        mixer.clipAction(clip).play();
      }
      mixers.push({ mixer, root: gltf.scene, clips: gltf.animations });
      registerMixer();
    }

    loadedCount++;
    onProgress?.(loadedCount, totalCount);
  };

  // --- Camera transition state ---
  const CAMERA_TRANSITION_DURATION_MS = 1000;
  let transitioning = false;
  let transitionStart = 0;
  const transitionFrom = new THREE.Vector3();
  const transitionTo = new THREE.Vector3();

  const sightPositions = new Map<SightId, THREE.Vector3>();
  for (const sight of sights) {
    sightPositions.set(sight.id, new THREE.Vector3(sight.position.x, sight.position.y, sight.position.z));
  }

  const navigateToSight = (sightId: SightId) => {
    const target = sightPositions.get(sightId);
    if (!target) return;
    transitioning = true;
    transitionStart = performance.now();
    transitionFrom.copy(controls.target);
    transitionTo.copy(target);
  };

  const updateTransition = () => {
    if (!transitioning) return;
    const elapsed = performance.now() - transitionStart;
    const t = Math.min(elapsed / CAMERA_TRANSITION_DURATION_MS, 1);
    const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
    controls.target.lerpVectors(transitionFrom, transitionTo, eased);
    if (t >= 1) transitioning = false;
  };

  // --- Preview model ---
  let previewModel: PreviewModel | null = null;
  let previewRevealStartTime: number | null = null;
  let previewDone = false;
  const revealDuration = config.preview?.revealDuration ?? DEFAULT_REVEAL_DURATION;

  // --- Camera fly after reveal ---
  const CAMERA_FLY_DURATION_MS = 2000;
  let cameraFlyStartTime: number | null = null;
  const cameraFlyFromPos = new THREE.Vector3();
  const cameraFlyToPos = new THREE.Vector3(camCfg.initialPosition.x, camCfg.initialPosition.y, camCfg.initialPosition.z);
  const cameraFlyFromTarget = new THREE.Vector3();
  const cameraFlyToTarget = new THREE.Vector3(
    camCfg.initialTarget?.x ?? ctrlCfg.target.x,
    camCfg.initialTarget?.y ?? ctrlCfg.target.y,
    camCfg.initialTarget?.z ?? ctrlCfg.target.z,
  );

  // --- Collision ---
  const COLLISION_STEP = 0.15;
  const COLLISION_MAX_STEPS = 8;
  let collisionSystem: MeshBVHCollisionSystem | null = null;
  let collisionMesh: THREE.Mesh | null = null;
  const lastSafePosition = new THREE.Vector3().copy(camera.position);
  const _scratchSubCurrent = new THREE.Vector3();
  const _scratchSubTarget = new THREE.Vector3();

  const buildCollision = () => {
    if (!config.collision) return;
    const result = buildCollisionBVH(modelRoots.values());
    if (!result) return;

    collisionSystem = new MeshBVHCollisionSystem(result.bvh, {
      cameraRadius: config.collision.cameraRadius ?? 0.05,
    });
    collisionMesh = new THREE.Mesh(result.geometry);
    collisionMesh.visible = false;
    lastSafePosition.copy(camera.position);
  };

  const applyCollision = () => {
    if (!collisionSystem) return;

    const desiredPosition = camera.position;
    const totalDist = lastSafePosition.distanceTo(desiredPosition);

    if (totalDist < collisionSystem.getConfig().epsilon) {
      lastSafePosition.copy(desiredPosition);
      return;
    }

    let constrainedPosition: THREE.Vector3;

    if (totalDist > COLLISION_STEP) {
      const steps = Math.min(
        Math.ceil(totalDist / COLLISION_STEP),
        COLLISION_MAX_STEPS,
      );
      const current = _scratchSubCurrent.copy(lastSafePosition);

      for (let i = 1; i <= steps; i++) {
        const t = i / steps;
        const target = _scratchSubTarget.lerpVectors(lastSafePosition, desiredPosition, t);
        const safe = collisionSystem.getSafePosition(current, target, { slide: false });
        if (safe.distanceTo(current) < collisionSystem.getConfig().epsilon) break;
        current.copy(safe);
      }
      constrainedPosition = current;
    } else {
      constrainedPosition = collisionSystem.getSafePosition(
        lastSafePosition,
        desiredPosition,
        { slide: false },
      );
    }

    if (!constrainedPosition.equals(desiredPosition)) {
      camera.position.copy(constrainedPosition);
      camera.lookAt(controls.target);
    }
    lastSafePosition.copy(camera.position);
  };

  if (config.preview) {
    previewModel = createPreviewModel(scene, config.preview, !suppressLoadingEvents);
    registerPreviewModel();
  }

  // --- POI markers ---
  let poiManager: PoiMarkerManager | null = null;

  const createPoiMarkers = () => {
    if (sights.length > 0) {
      poiManager = new PoiMarkerManager({
        container,
        gestureTarget: renderer.domElement,
        onNavigateToSight: (sightId: SightId) => {
          navigateToSight(sightId);
          userSelectedSight({ id: sightId, smooth: true, source: 'poi' });
          assistantPoiClickRequested({ id: sightId });
        },
        initialConfig: defaultPoiConfig,
      });
      poiManager.createMarkers(sights);
      registerPoiManager();
    }
  };

  const loadAll = async () => {
    try {
      let previewReadyTime = 0;

      const previewPromise = previewModel?.loadPromise.then(() => {
        if (disposed) return;
        if (!suppressLoadingEvents) scenePreviewLoadingFinished();
        onPreviewReady?.();
        controls.autoRotate = true;
        controls.autoRotateSpeed = PREVIEW_AUTO_ROTATE_SPEED;
        previewReadyTime = performance.now();
      });

      const modelsPromise = Promise.all(config.models.map(loadModel));

      await Promise.all([previewPromise, modelsPromise].filter(Boolean));
      if (disposed) return;

      if (previewModel) {
        // Ensure preview is visible for at least MIN_PREVIEW_DISPLAY_MS
        const elapsed = performance.now() - previewReadyTime;
        const remaining = MIN_PREVIEW_DISPLAY_MS - elapsed;
        if (remaining > 0) {
          await new Promise<void>((r) => setTimeout(r, remaining));
        }
        if (disposed) return;

        // Start reveal animation (main models stay hidden until reveal completes)
        controls.autoRotate = false;
        previewModel.setRevealOrigin(camera.position);
        previewRevealStartTime = performance.now();
      } else {
        buildCollision();
        createPoiMarkers();
        onReady?.();
      }
    } catch (err) {
      if (disposed) return;
      console.error('[createGlbScene] Failed to load models', err);
      onError?.(err);
    }
  };

  loadAll();

  // --- Camera debug overlay ---
  let debugEl: HTMLElement | null = null;
  const cameraDebug = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('camera-debug');
  if (cameraDebug) {
    debugEl = document.createElement('div');
    Object.assign(debugEl.style, {
      position: 'fixed', bottom: '12px', left: '12px', zIndex: '9999',
      background: 'rgba(0,0,0,0.75)', color: '#0f0', padding: '8px 12px',
      fontFamily: 'monospace', fontSize: '12px', borderRadius: '6px',
      pointerEvents: 'none', whiteSpace: 'pre',
    });
    document.body.appendChild(debugEl);
  }

  // --- Render loop ---
  let heartbeatFrame = 0;
  const animate = () => {
    if (disposed) return;
    frameId = requestAnimationFrame(animate);
    heartbeatFrame++;
    // Heartbeat every ~60 frames (≈1sec @ 60fps) to confirm loop liveness.
    if (heartbeatFrame % 60 === 0) {
      logEvent('createGlbScene frame', {
        sceneId: config.id,
        poiArtifact: poiArtifactOverlay,
        frame: heartbeatFrame,
        paused,
        canvasConnected: renderer.domElement.isConnected,
        canvasParentCls: (renderer.domElement.parentElement?.className ?? 'null').toString().slice(0, 40),
        containerSize: `${container.clientWidth}x${container.clientHeight}`,
        drawSize: `${renderer.domElement.width}x${renderer.domElement.height}`,
      });
    }
    if (paused) return;

    const delta = clock.getDelta();
    const currentTime = clock.getElapsedTime();

    updateTransition();
    controls.update();
    applyCollision();
    principalPointController.update(currentTime);
    for (const entry of mixers) {
      entry.mixer.update(delta);
    }

    // Update preview model with reveal animation
    if (previewModel && !previewDone) {
      const revealElapsed = previewRevealStartTime != null
        ? (performance.now() - previewRevealStartTime) / 1000
        : 0;

      previewModel.update({
        time: currentTime,
        splatRevealTime: revealElapsed,
      });

      // Reveal complete — show main models, dispose preview, fly camera to main position
      if (previewRevealStartTime != null && revealElapsed >= revealDuration) {
        previewDone = true;
        for (const root of modelRoots.values()) {
          root.visible = true;
        }
        previewModel.dispose();
        previewModel = null;
        scene.background = mainBgColor;
        renderer.setClearColor(resolvedClearColor);

        // Start camera fly to main scene position
        if (previewCam?.cameraPosition) {
          cameraFlyFromPos.copy(camera.position);
          cameraFlyFromTarget.copy(controls.target);
          cameraFlyStartTime = performance.now();
        }

        buildCollision();
        createPoiMarkers();
        if (!suppressLoadingEvents) sceneLoadingFinished();
        onReady?.();
      }
    }

    // Animate camera fly from preview to main position
    if (cameraFlyStartTime != null) {
      const flyElapsed = performance.now() - cameraFlyStartTime;
      const t = Math.min(flyElapsed / CAMERA_FLY_DURATION_MS, 1);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      camera.position.lerpVectors(cameraFlyFromPos, cameraFlyToPos, eased);
      controls.target.lerpVectors(cameraFlyFromTarget, cameraFlyToTarget, eased);
      if (t >= 1) cameraFlyStartTime = null;
    }

    poiManager?.update(camera, canvasWidth, canvasHeight);

    if (debugEl) {
      const p = camera.position;
      const t = controls.target;
      debugEl.textContent =
        `pos: { x: ${p.x.toFixed(3)}, y: ${p.y.toFixed(3)}, z: ${p.z.toFixed(3)} }\n` +
        `tgt: { x: ${t.x.toFixed(3)}, y: ${t.y.toFixed(3)}, z: ${t.z.toFixed(3)} }\n` +
        `dist: ${p.distanceTo(t).toFixed(3)}  fov: ${camera.fov}`;
    }

    renderer.render(scene, camera);
  };
  animate();

  // --- Mutable config for swapContent ---
  let currentConfig = config;
  // Generation counter — increments on each swapContent call so in-flight GLB
  // loads from a previous generation can detect they've been superseded and
  // bail out (no addition to scene, no onReady).
  let contentGeneration = 0;

  // --- API ---
  // disposeMaterialTextures imported from `../utils/disposeSceneResources`.
  // Earlier this file had its own LOCAL copy that DIDN'T null `texture.source.data`
  // (the CPU-side ImageBitmap / Float32Array), causing artifact swaps to leak
  // ~10-50 MB CPU heap per cycle on iOS until OOM. Removed in favor of the
  // shared helper that does both GPU dispose + CPU buffer null + iterates
  // EVERY texture-typed field (catches PBR-physical clearcoat / transmission
  // / sheen / iridescence / anisotropy maps that the old hand-coded list missed).
  // See docs/architecture/webgl-memory.md + docs/architecture/overlays.md for the full investigation.

  /**
   * Dispose only CONTENT-specific resources:
   * - models (geometry + materials + textures) via traverse on each modelRoot
   * - mixers (stopAllAction + uncache*)
   * - collisionSystem + collisionMesh
   * - poiManager
   * - previewModel
   * - groundPlane
   *
   * Does NOT touch shell (renderer/scene-root/camera/controls/lighting/envMap/canvas).
   * Safe to call multiple times; idempotent (all refs become null/empty).
   */
  const unloadContent = () => {
    // ARTIFACT DEBUG: register all current textures/materials/geometries
    // for finalization tracking BEFORE we dispose them. After ~5s we check
    // if GC actually collected them. If not — leak.
    if (poiArtifactOverlay) {
      const tracked = trackTreeForFinalization(modelRoots.values(), currentConfig.id);
      logArtifactPhase('unloadContent.start', {
        renderer,
        scene,
        sceneId: currentConfig.id,
        extra: {
          modelRoots: modelRoots.size,
          mixers: mixers.length,
          hasCollision: !!collisionSystem,
          hasPoi: !!poiManager,
          hasPreview: !!previewModel,
          hasGround: !!groundPlane,
          tracked,
        },
      });
    }
    // Cancel any running preview reveal / camera fly animations
    previewDone = false;
    previewRevealStartTime = null;
    cameraFlyStartTime = null;
    transitioning = false;

    // Models: remove from scene, then traverse for geometry/material/texture dispose
    const rootsToDispose = Array.from(modelRoots.values());
    for (const root of rootsToDispose) {
      scene.remove(root);
    }
    for (const root of rootsToDispose) {
      root.traverse((obj) => {
        if (obj instanceof THREE.Mesh) {
          obj.geometry?.dispose();
          const materials = Array.isArray(obj.material) ? obj.material : [obj.material];
          for (const mat of materials) {
            if (mat && typeof mat.dispose === 'function') {
              disposeMaterialTextures(mat);
              mat.dispose();
            }
          }
        }
      });
    }
    modelRoots.clear();

    // Mixers
    const mixerCount = mixers.length;
    for (const { mixer, root, clips } of mixers) {
      mixer.stopAllAction();
      for (const clip of clips) {
        mixer.uncacheAction(clip, root);
        mixer.uncacheClip(clip);
      }
      mixer.uncacheRoot(root);
    }
    mixers.length = 0;
    for (let i = 0; i < mixerCount; i++) unregisterMixer();

    // Collision
    if (collisionSystem) {
      collisionSystem.dispose();
      collisionSystem = null;
    }
    if (collisionMesh) {
      collisionMesh.geometry.dispose();
      collisionMesh = null;
    }

    // POI manager
    if (poiManager) {
      poiManager.dispose();
      poiManager = null;
      unregisterPoiManager();
    }

    // Preview model
    if (previewModel) {
      previewModel.dispose();
      previewModel = null;
      unregisterPreviewModel();
    }

    // Ground plane
    if (groundPlane) {
      scene.remove(groundPlane);
      groundPlane.geometry?.dispose();
      const gpMat = Array.isArray(groundPlane.material) ? groundPlane.material[0] : groundPlane.material;
      if (gpMat && typeof gpMat.dispose === 'function') {
        disposeMaterialTextures(gpMat);
        gpMat.dispose();
      }
      groundPlane = null;
    }

    // ARTIFACT DEBUG: snapshot AFTER full unload — what's left in scene/renderer.info?
    // Compare to unloadContent.start: textures/geometries/materials should drop.
    // If they don't drop — the dispose() calls aren't reaching everything.
    if (poiArtifactOverlay) {
      logArtifactPhase('unloadContent.done', {
        renderer,
        scene,
        sceneId: currentConfig.id,
        extra: {
          modelRoots: modelRoots.size,
          mixers: mixers.length,
        },
      });
    }
  };

  /**
   * Reset camera position, target, and OrbitControls limits from a config.
   * Called on swapContent to apply the new artifact's camera framing without
   * recreating the camera itself (FOV/near/far stay shell-invariant).
   */
  const applyCameraAndControlsFromConfig = (cfg: GlbSceneConfig) => {
    const cCfg = cfg.camera;
    const pos = cCfg.initialPosition;
    const tgt = cCfg.initialTarget ?? { x: 0, y: 0, z: 0 };
    camera.position.set(pos.x, pos.y, pos.z);
    camera.updateProjectionMatrix();

    const ccCfg = cfg.controls;
    controls.target.set(ccCfg.target.x, ccCfg.target.y, ccCfg.target.z);
    controls.minDistance = ccCfg.minDistance;
    controls.maxDistance = ccCfg.maxDistance;
    controls.minPolarAngle = ccCfg.minPolarAngle ?? 0;
    controls.maxPolarAngle = ccCfg.maxPolarAngle ?? Math.PI;
    if (ccCfg.minAzimuthAngle != null || ccCfg.maxAzimuthAngle != null) {
      const initialAzimuth = Math.atan2(pos.x - tgt.x, pos.z - tgt.z);
      controls.minAzimuthAngle = ccCfg.minAzimuthAngle != null
        ? ccCfg.minAzimuthAngle + initialAzimuth
        : -Infinity;
      controls.maxAzimuthAngle = ccCfg.maxAzimuthAngle != null
        ? ccCfg.maxAzimuthAngle + initialAzimuth
        : Infinity;
    } else {
      controls.minAzimuthAngle = -Infinity;
      controls.maxAzimuthAngle = Infinity;
    }
    if (ccCfg.enablePan != null) controls.enablePan = ccCfg.enablePan;
    controls.enableDamping = ccCfg.enableDamping ?? true;
    controls.dampingFactor = ccCfg.dampingFactor ?? 0.05;
    if (ccCfg.autoRotate != null) controls.autoRotate = ccCfg.autoRotate;
    if (ccCfg.autoRotateSpeed != null) controls.autoRotateSpeed = ccCfg.autoRotateSpeed;
    controls.update();

    // Reset lastSafePosition (used by collision) so it doesn't snap the camera back
    lastSafePosition.copy(camera.position);
  };

  /**
   * Preloaded entry: Object3D + его animations + source entry (для applyTransform).
   * Создаётся в CPU-памяти через fetch + parse — НЕ добавлен в scene, НЕ
   * uploads текстуры в GPU. Upload произойдёт лениво при первом render'е
   * после scene.add().
   */
  type PreloadedEntry = {
    entry: GlbModelEntry;
    root: THREE.Object3D;
    animations: THREE.AnimationClip[];
  };

  /**
   * Pre-load content на CPU: fetch ArrayBuffer + parse в Three.js scene graph.
   * НЕ добавляет в scene — textures/geometries живут в JS-памяти, GPU upload
   * отложен до первого рендера после scene.add.
   *
   * Это ключ к iOS OOM fix: пока старая модель всё ещё в scene (и GPU),
   * мы **только** allocate на CPU (много свободной оперативки). Когда
   * preload готов — unload старую (GPU освобождается), затем add новую
   * (GPU alloc). Никакого overlap GPU-памяти.
   */
  const preloadContentFromConfig = async (
    cfg: GlbSceneConfig,
    gen: number,
  ): Promise<PreloadedEntry[]> => {
    const { loader: gltfLoader } = getSharedLoaders();
    const objLoaderForPreload = new OBJLoader();

    const preloadEntry = async (entry: GlbModelEntry): Promise<PreloadedEntry | null> => {
      const isObj = entry.url.toLowerCase().endsWith('.obj');
      const resp = await loadWithCdnFallback(
        entry.url,
        async (url) => {
          const r = await fetchWithTtfbTimeout(url);
          if (!r.ok) throw new Error(`Failed to fetch ${url}: ${r.status}`);
          return r;
        },
        { assetType: 'glb', sceneId: cfg.id },
      );
      if (disposed || gen !== contentGeneration) return null;

      if (isObj) {
        const text = await resp.text();
        if (disposed || gen !== contentGeneration) return null;
        const group = objLoaderForPreload.parse(text);
        const material = new THREE.MeshStandardMaterial({ color: 0x888888 });
        group.traverse((child) => {
          if (child instanceof THREE.Mesh) child.material = material;
        });
        return { entry, root: group, animations: [] };
      }

      const buffer = await resp.arrayBuffer();
      if (disposed || gen !== contentGeneration) return null;
      // parse: GLTFLoader decodes binary + creates geometries/materials/textures
      // in JS memory. DRACO decode runs in worker. No GPU upload until render.
      const gltf = await new Promise<GLTF>((resolve, reject) => {
        gltfLoader.parse(buffer, '', resolve, reject);
      });
      return { entry, root: gltf.scene, animations: gltf.animations };
    };

    const results = await Promise.all(cfg.models.map(preloadEntry));
    return results.filter((r): r is PreloadedEntry => r !== null);
  };

  /** Dispose списка PreloadedEntry (если stale и не будут добавлены в scene). */
  const disposePreloaded = (preloaded: PreloadedEntry[]) => {
    for (const { root } of preloaded) {
      root.traverse((obj) => {
        if (obj instanceof THREE.Mesh) {
          obj.geometry?.dispose();
          const materials = Array.isArray(obj.material) ? obj.material : [obj.material];
          for (const mat of materials) {
            if (mat && typeof mat.dispose === 'function') {
              disposeMaterialTextures(mat);
              mat.dispose();
            }
          }
        }
      });
    }
  };

  /** Добавить pre-loaded content в scene. GPU upload произойдёт на первом render. */
  const attachPreloadedContent = (preloaded: PreloadedEntry[], cfg: GlbSceneConfig) => {
    for (const { entry, root, animations } of preloaded) {
      applyTransform(root, entry);
      scene.add(root);
      modelRoots.set(entry.key, root);
      if (entry.playAnimations && animations.length > 0) {
        const mixer = new THREE.AnimationMixer(root);
        for (const clip of animations) mixer.clipAction(clip).play();
        mixers.push({ mixer, root, clips: animations });
        registerMixer();
      }
    }
    if (cfg.collision) {
      const result = buildCollisionBVH(modelRoots.values());
      if (result) {
        collisionSystem = new MeshBVHCollisionSystem(result.bvh, {
          cameraRadius: cfg.collision.cameraRadius ?? 0.05,
        });
        collisionMesh = new THREE.Mesh(result.geometry);
        collisionMesh.visible = false;
        lastSafePosition.copy(camera.position);
      }
    }
  };

  /**
   * Swap content без теми́нг-based delay. Порядок:
   * 1. Pre-load новый GLB на CPU (fetch + parse). Пока грузится — старая
   *    модель остаётся в scene, видна пользователю.
   * 2. Когда pre-load готов → unloadContent (освобождаем GPU от старой).
   * 3. await rAF × 2 чтобы iOS успел flush GPU command queue.
   * 4. attachPreloadedContent (upload новой в GPU).
   * 5. onReady.
   *
   * Отсутствие overlap'а GPU-памяти решает iOS OOM: в любой момент в GPU
   * висит либо старое, либо новое — но не оба одновременно.
   */
  const swapContent = (newCfg: GlbSceneConfig, onReady?: () => void): void => {
    if (disposed) {
      logEvent('createGlbScene swapContent NOOP (disposed)', { sceneId: newCfg.id });
      return;
    }
    if (newCfg.preview != null) {
      // Preview reveal not supported in swap path.
      console.warn('[createGlbScene] swapContent called with preview config — ignoring preview');
    }
    const gen = ++contentGeneration;
    const swapStart = performance.now();
    logEvent('createGlbScene swapContent start (preload)', {
      fromSceneId: currentConfig.id,
      toSceneId: newCfg.id,
      gen,
    });

    // ARTIFACT DEBUG: phase 1 — start of swap, BEFORE preload allocates anything.
    // Compare to subsequent phases to find where memory grows / fails to release.
    if (poiArtifactOverlay) {
      logArtifactPhase('swap.start', {
        renderer,
        scene,
        sceneId: currentConfig.id,
        extra: { fromSceneId: currentConfig.id, toSceneId: newCfg.id, gen },
      });
      dumpFinalizationStats(`swap.start gen=${gen}`);
    }

    preloadContentFromConfig(newCfg, gen)
      .then((preloaded) => {
        if (disposed) {
          disposePreloaded(preloaded);
          return;
        }
        if (gen !== contentGeneration) {
          logEvent('createGlbScene swapContent STALE (preload)', {
            gen,
            currentGen: contentGeneration,
          });
          disposePreloaded(preloaded);
          return;
        }
        logEvent('createGlbScene swapContent preload done', {
          sceneId: newCfg.id,
          gen,
          preloadMs: +(performance.now() - swapStart).toFixed(1),
          preloadedCount: preloaded.length,
        });

        // ARTIFACT DEBUG: phase 2 — preload done. New textures DECODED in CPU
        // memory but not yet uploaded to GPU. Old content still in scene/GPU.
        // Heap should grow by ~size of new GLB textures decoded.
        if (poiArtifactOverlay) {
          logArtifactPhase('swap.preload-done', {
            renderer,
            scene,
            sceneId: currentConfig.id,
            extra: { newSceneId: newCfg.id, gen, preloadedCount: preloaded.length },
          });
        }

        // Теперь unload старого — GPU начинает освобождаться
        unloadContent();
        currentConfig = newCfg;
        applyCameraAndControlsFromConfig(newCfg);

        // 2 rAF чтобы render loop прошёл 2 кадра с пустой сценой.
        // Это толкает iOS flush pending GPU commands (dispose queue).
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (disposed || gen !== contentGeneration) {
              disposePreloaded(preloaded);
              return;
            }
            attachPreloadedContent(preloaded, newCfg);
            logEvent('createGlbScene swapContent done', {
              sceneId: newCfg.id,
              gen,
              totalMs: +(performance.now() - swapStart).toFixed(1),
              modelRoots: modelRoots.size,
            });

            // ARTIFACT DEBUG: phase 4 — attach done. New content in scene + GPU.
            // Schedule a +5s finalization check: did GC actually collect the
            // textures/materials/geometries we registered in unloadContent?
            if (poiArtifactOverlay) {
              logArtifactPhase('swap.attach-done', {
                renderer,
                scene,
                sceneId: newCfg.id,
                extra: { gen, totalMs: +(performance.now() - swapStart).toFixed(1) },
              });
              scheduleFinalizationCheck(`swap ${currentConfig.id} → ${newCfg.id}`, 5000);
            }
            onReady?.();
          });
        });
      })
      .catch((err) => {
        if (disposed) return;
        console.error('[createGlbScene] swapContent preload failed', err);
      });
  };

  const dispose = () => {
    const disposeStart = performance.now();
    logEvent('createGlbScene dispose start', {
      sceneId: currentConfig.id,
      poiArtifact: poiArtifactOverlay,
      mixers: mixers.length,
      modelRoots: modelRoots.size,
    });
    disposed = true;
    // Invalidate any in-flight swapContent — increment generation so late
    // callbacks bail out.
    contentGeneration++;
    stopConfigWatch?.();
    cancelAnimationFrame(frameId);
    resizeObserver.disconnect();
    controls.dispose();

    // Content cleanup (models, mixers, collision, poi, preview, groundPlane)
    unloadContent();

    debugEl?.remove();

    // Shell-only: environment map + any stray mesh (lighting helpers, debug)
    envRenderTarget.dispose();
    scene.environment = null;
    scene.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.geometry?.dispose();
        const materials = Array.isArray(obj.material) ? obj.material : [obj.material];
        for (const mat of materials) {
          if (mat && typeof mat.dispose === 'function') {
            disposeMaterialTextures(mat);
            mat.dispose();
          }
        }
      }
    });

    if (poiArtifactOverlay) {
      // Dedicated renderer for artifact overlay — full teardown of context.
      renderer.renderLists.dispose();
      renderer.dispose();
      stopContextLossTracking?.();
      stopContextLossTracking = null;
      renderer.forceContextLoss();
      renderer.domElement.remove();
      decrementWebGLContext('glb');
    } else {
      // Shared main renderer — detach + reset state only.
      releaseMainRenderer(mainRendererClaimantId);
    }
    decrementScene('glb');
    logEvent('createGlbScene dispose done', {
      sceneId: currentConfig.id,
      poiArtifact: poiArtifactOverlay,
      durationMs: +(performance.now() - disposeStart).toFixed(1),
    });
  };

  const pause = () => {
    if (disposed || paused) return;
    paused = true;
    logEvent('createGlbScene pause', {
      sceneId: config.id,
      poiArtifact: poiArtifactOverlay,
      canvasConnected: renderer.domElement.isConnected,
      canvasParentCls: (renderer.domElement.parentElement?.className ?? 'null').toString().slice(0, 40),
      containerSize: `${container.clientWidth}x${container.clientHeight}`,
    });
  };

  /**
   * Standby: выгружаем content (модели + текстуры + collision + mixers + poi)
   * из GPU, но сохраняем shell (renderer, scene, camera, controls, lighting,
   * envMap). Render loop встаёт. Canvas отсоединяется от DOM.
   *
   * Инвалидируем in-flight swapContent через contentGeneration++, чтобы
   * поздние preload callback'и не повесили content обратно.
   */
  const standby = () => {
    if (disposed) {
      logEvent('createGlbScene standby NOOP (disposed)', { sceneId: currentConfig.id });
      return;
    }
    const standbyStart = performance.now();
    logEvent('createGlbScene standby start', {
      sceneId: currentConfig.id,
      poiArtifact: poiArtifactOverlay,
      modelRoots: modelRoots.size,
      mixers: mixers.length,
    });
    // Инвалидируем любые in-flight swapContent — их поздние attach не должны
    // сработать после standby.
    contentGeneration++;
    unloadContent();
    if (!paused) {
      paused = true;
    }
    if (renderer.domElement.parentElement) {
      renderer.domElement.remove();
    }
    logEvent('createGlbScene standby done', {
      sceneId: currentConfig.id,
      durationMs: +(performance.now() - standbyStart).toFixed(1),
    });

    // ARTIFACT DEBUG: schedule finalization check after standby. If user
    // closes artifact and waits, we should see all textures/materials/
    // geometries finalized within ~5s. If not — leak in standby path.
    if (poiArtifactOverlay) {
      logArtifactPhase('standby.done', {
        renderer,
        scene,
        sceneId: 'standby',
        extra: {},
      });
      scheduleFinalizationCheck(`standby ${currentConfig.id}`, 5000);
    }
  };

  const resume = (newContainer?: HTMLElement) => {
    if (disposed) {
      logEvent('createGlbScene resume NOOP (disposed)', { sceneId: config.id });
      return;
    }
    const oldParentCls = (renderer.domElement.parentElement?.className ?? 'null').toString().slice(0, 40);
    const connectedBefore = renderer.domElement.isConnected;
    const pausedBefore = paused;
    const target = newContainer ?? container;
    const migrated = target !== container;
    if (migrated) {
      // Switching to a different container: unobserve old, observe new.
      resizeObserver.unobserve(container);
      container = target;
      resizeObserver.observe(target);
    }
    // Re-attach canvas if it was detached (pause + releasePoiArtifact
    // calls renderer.domElement.remove()). Must run even when
    // target === container, otherwise scroll-away → scroll-back leaves
    // canvas dangling and preview shows blank background.
    const wasDetached = renderer.domElement.parentElement !== target;
    if (wasDetached) {
      target.appendChild(renderer.domElement);
    }
    if (migrated || wasDetached) {
      setRendererSize();
    }
    if (paused) {
      paused = false;
      // Reset delta so first frame after resume doesn't have a huge dt
      clock.getDelta();
    }
    logEvent('createGlbScene resume', {
      sceneId: config.id,
      poiArtifact: poiArtifactOverlay,
      migrated,
      wasDetached,
      pausedBefore,
      pausedAfter: paused,
      containerSize: `${container.clientWidth}x${container.clientHeight}`,
      oldParentCls,
      newParentCls: (renderer.domElement.parentElement?.className ?? 'null').toString().slice(0, 40),
      connectedBefore,
      connectedAfter: renderer.domElement.isConnected,
      drawSize: `${renderer.domElement.width}x${renderer.domElement.height}`,
    });
  };

  // ARTIFACT DEBUG: install window.__dumpArtifact() console helper.
  // Call from DevTools to manually snapshot artifact-side state at any
  // moment (between user actions). No-op when ?mem-debug=1 is not set.
  // Only one artifact-host is alive at a time, so we don't worry about
  // overwriting; uninstall on dispose.
  let uninstallDumpHook: (() => void) | undefined;
  if (poiArtifactOverlay) {
    uninstallDumpHook = installDumpHook(() => ({
      sceneId: currentConfig.id,
      renderer,
      scene,
      extra: {
        modelRoots: modelRoots.size,
        mixers: mixers.length,
        hasCollision: !!collisionSystem,
        hasPoi: !!poiManager,
        hasGround: !!groundPlane,
        paused,
        canvasConnected: renderer.domElement.isConnected,
      },
    }));
  }
  // Wrap dispose to clean up the dump hook installed above.
  const wrappedDispose = () => {
    uninstallDumpHook?.();
    dispose();
  };

  return {
    dispose: wrappedDispose,
    pause,
    resume,
    swapContent,
    standby,
    scene,
    renderer,
    camera,
    controls,
    hemisphereLight: hemiLight,
    directionalLights,
    modelRoots,
    get groundPlane() { return groundPlane; },
    get collisionSystem() { return collisionSystem; },
    get collisionMesh() { return collisionMesh; },
  };
}
